import { Check, Minus } from "@phosphor-icons/react";
import { useLayoutEffect, useState } from "react";

import { logout } from "@/lib/api";
import { logger } from "@/lib/logger";

import { PillButton } from "./components";
import { PRODUCT_NAMES } from "./product-names";

/**
 * Flat Background-Yellow block behind the hero photo. The handoff is a daylight
 * moment, so it takes the brand's warm tint rather than any one module's.
 */
const DAYLIGHT_HERO = "bg-daylight-subtle";

interface OffboardingScreenProps {
  /**
   * Fired after the session has been ended and the goodbye is on screen.
   * Optional because there is nowhere for the flow to go — see `handleClose`.
   */
  onClose?: () => void;
  /**
   * Modules skipped along the way. The screen's claim shrinks to match:
   * "everything is connected" is only said when it is true.
   */
  skippedSteps?: string[];
}

/**
 * The signed-off state, shown in place after the household closes the flow.
 *
 * Deliberately says what is actually true rather than pretending the page can
 * dismiss itself: a browser tab cannot close itself unless a script opened it,
 * and this one arrived from a QR code on a cardboard box.
 */
function SignedOff() {
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col items-center justify-center gap-4 bg-white px-8 text-center">
      <div className="bg-success-subtle flex size-[72px] items-center justify-center rounded-full">
        <div className="bg-success flex size-14 items-center justify-center rounded-full">
          <Check weight="bold" className="size-[26px] text-white" />
        </div>
      </div>
      <h1 className="text-primary type-title">You can close this tab</h1>
      <p className="text-muted text-[17px] leading-6">
        Scanning the QR code on your kit brings your setup back any time.
      </p>
      <p className="text-muted type-body-sm pt-2">Part of the SmartClimate beta program.</p>
    </div>
  );
}

/**
 * Figma 1303:835 "Phase 6 · App Handoff" — the last screen of the flow.
 *
 * It has two jobs, and the second is the one that matters. Confirming the
 * install is done is easy; the screen also has to hand the household over to a
 * wait. They finish with hardware humming and nothing to touch, and the app
 * they were promised arrives days later by email. Saying so plainly, with the
 * number of days, is what stops that gap reading as the flow having failed at
 * the last step.
 *
 * Terminal: `FlowShell` renders this once every step including `complete` is
 * done, replacing the bare check-in-a-circle that used to sit inline there.
 */
export function OffboardingScreen({ onClose, skippedSteps = [] }: OffboardingScreenProps) {
  const [closed, setClosed] = useState(false);

  // Neither this screen nor `SignedOff` renders an AppBar (there's nowhere to
  // go back to on the last screen of the flow), so the swap from the goodbye
  // view to `SignedOff` needs its own reset: without it, closing from partway
  // down this screen's scroll would land `SignedOff` mid-page. Layout effect
  // so the swap is never painted at the old offset first.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [closed]);

  /**
   * End the session, then show the goodbye in place.
   *
   * Two deliberate choices here.
   *
   * The raw SDK `logout()` rather than the `useLogout()` hook: that hook calls
   * `queryClient.clear()`, which would drop the onboarding state this screen is
   * mounted under, unmount it mid-goodbye, and land the household on the code
   * form — the exact dead-end the flyer-link work exists to prevent. Ending the
   * server session while leaving the cache alone lets the goodbye actually
   * render. A reload afterwards goes to code entry, which is correct: the
   * session really is over, and the kit's QR signs them back in with no typing.
   *
   * `setClosed` first, and a swallowed error: the install is finished and the
   * data is saved. Whether the logout call succeeds changes nothing the
   * household can act on, so a network blip must not turn a goodbye into an
   * error message.
   */
  const handleClose = () => {
    setClosed(true);
    void logout()
      .catch((err: unknown) => logger.warn("[onboarding] logout on close failed:", err))
      .finally(() => onClose?.());
  };

  if (closed) {
    return <SignedOff />;
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col gap-6 bg-white py-6">
      <div className="flex w-full flex-col items-center gap-4 px-6">
        <p className="text-success type-eyebrow">SETUP COMPLETE</p>
        <div className="bg-success-subtle flex size-[72px] items-center justify-center rounded-full">
          <div className="bg-success flex size-14 items-center justify-center rounded-full">
            <Check weight="bold" className="size-[26px] text-white" />
          </div>
        </div>
        <h1 className="text-primary type-title text-center">
          {skippedSteps.length > 0 ? "Your home is set up" : "Your home is all set"}
        </h1>
        <p className="text-muted text-center text-[17px] leading-6">
          {skippedSteps.length > 0
            ? `Everything else is connected. The ${PRODUCT_NAMES.hub} will start to listen.`
            : `Everything is connected. The ${PRODUCT_NAMES.hub} will start to listen.`}
        </p>
        {/* Skipped modules are named on the last screen a household sees, in
            the same yellow the guide's overview gave them. This screen used to
            say "everything is connected" over hardware still in its box, which
            is the other half of VLX-76: the overview hid skips behind a green
            check, and this screen hid them behind a plural. */}
        {skippedSteps.length > 0 && (
          <div className="bg-daylight-subtle flex w-full flex-col gap-2 rounded-2xl p-4">
            <p className="text-warning type-label">Not set up</p>
            {skippedSteps.map((name) => (
              <div key={name} className="flex items-center gap-3">
                <div className="bg-daylight flex size-6 shrink-0 items-center justify-center rounded-full">
                  <Minus weight="bold" className="text-primary size-3.5" aria-hidden />
                </div>
                <p className="text-primary text-[15px] leading-[22px]">{name}</p>
              </div>
            ))}
            <p className="text-muted text-[15px] leading-[22px]">
              Skipped during setup and not running yet. VELUX will reach out about this.
            </p>
          </div>
        )}
        <div className={`h-[281px] w-full overflow-hidden rounded-2xl ${DAYLIGHT_HERO}`}>
          {/* Pre-cropped to the region the frame shows, so this is a plain
              cover fill rather than a 470px-tall image slid up by 94px. */}
          <img
            src="/brand/onboarding/offboarding-hero.jpg"
            alt="A sunlit attic room with VELUX roof windows"
            className="size-full object-cover"
          />
        </div>
      </div>

      <div className="w-full px-6">
        {/*
         * Whole sentence at full contrast, emphasis carried by weight — bold
         * applied mid-sentence for emphasis, not as a type role (captions are
         * bold app-wide now, so that's no longer a distinction worth claiming).
         *
         * Emphasis by colour was the first attempt and was worse: dimming the
         * surrounding words to make the phrase stand out dropped the sentence
         * to 2.91:1, and that sentence is the whole point of the screen — it is
         * the only place a household is told when their app arrives.
         */}
        <p className="text-primary text-center text-[17px] leading-6">
          You will receive an email within <span className="font-bold">3-4 days with a link</span>{" "}
          to test the SmartClimate app.
        </p>
      </div>

      {/* The close is a ghost, not the orange pill the frame draws. Nothing on
          this screen needs doing — the household's next real move is opening an
          email in a few days — so a full-width primary CTA gave the loudest
          element on the page to its least useful action, outranking the one
          sentence that tells them when their app arrives. */}
      <div className="flex w-full flex-col items-center gap-3 px-6 pt-4">
        <PillButton variant="ghost" label="Close the onboarding flow" onClick={handleClose} />
        <p className="text-muted type-body-sm text-center">
          Part of the SmartClimate beta program.
        </p>
      </div>
    </div>
  );
}
